import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Download, Info } from "lucide-react";
import {
  GreekStateInterestCalculator,
  type CalculationResult,
  type InterestType,
} from "@/lib/calculator";
import {
  buildInterestWorkbookBuffer,
  createInterestWorkbookFilename,
} from "@/lib/excel-export";
import {
  formatGreekMoneyInput,
  parseGreekMoneyInput,
  sanitizeGreekMoneyDraft,
} from "@/lib/money";
import { fetchMroHistory, type MroHistoryResult } from "@/lib/mro-source";

function formatCurrency(value: number): string {
  return value.toLocaleString("el-GR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

function formatRate(rate: number): string {
  return rate
    .toLocaleString("el-GR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .concat("%");
}

function formatInterestType(type: InterestType): string {
  return type === "epidikias" ? "Τόκοι επιδικίας" : "Τόκοι υπερημερίας";
}

function toISODateStr(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function InterestCalculator() {
  const [principal, setPrincipal] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [yearBase, setYearBase] = useState("365");
  const [interestType, setInterestType] = useState<InterestType>("yperimerias");
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Ξεκινά αμέσως με το ενσωματωμένο ιστορικό MRO και αναβαθμίζεται στο live
  // API μόλις απαντήσει· σε αποτυχία του API παραμένει το ενσωματωμένο.
  const [calculator, setCalculator] = useState(
    () => new GreekStateInterestCalculator()
  );
  const [mroSource, setMroSource] = useState<MroHistoryResult["source"]>("bundled");

  useEffect(() => {
    let cancelled = false;
    fetchMroHistory().then((res) => {
      if (cancelled || res.source !== "api") return;
      setCalculator(new GreekStateInterestCalculator(res.history));
      setMroSource("api");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setError(null);
    setResult(null);
    setExportError(null);

    const principalNum = parseGreekMoneyInput(principal);
    if (principalNum === null || principalNum <= 0) return;
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return;
    }

    try {
      const res = calculator.calculateInterest(
        principalNum,
        toISODateStr(startDate),
        toISODateStr(endDate),
        parseInt(yearBase, 10),
        interestType
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [principal, startDate, endDate, yearBase, interestType, calculator]);

  async function handleExcelDownload() {
    if (!result || !startDate || !endDate) {
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      const input = {
        principal: result.principalAmount,
        startDateStr: toISODateStr(startDate),
        endDateStr: toISODateStr(endDate),
        daysInYear: parseInt(yearBase, 10),
        interestType: result.interestType,
      };
      const buffer = await buildInterestWorkbookBuffer(input, result, calculator);
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = createInterestWorkbookFilename(input);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setExportError(
        err instanceof Error
          ? err.message
          : "Αποτυχία δημιουργίας του αρχείου Excel."
      );
    } finally {
      setIsExporting(false);
    }
  }

  function handlePrincipalChange(value: string) {
    setPrincipal(sanitizeGreekMoneyDraft(value));
  }

  function handlePrincipalBlur() {
    const principalNum = parseGreekMoneyInput(principal);
    if (principalNum !== null && principalNum > 0) {
      setPrincipal(formatGreekMoneyInput(principalNum));
    }
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            Υπολογιστής Τόκων κατά του Δημοσίου
          </CardTitle>
          <CardDescription>
            Σύμφωνα με το Άρθρο 45 του Ν. 4607/2019. Για περιόδους πριν την
            01/05/2019 εφαρμόζεται το σταθερό επιτόκιο 6%. Από 01/05/2019 έως
            13/09/2022 λαμβάνεται υπόψη το MRO της επίδοσης, η οποία για το
            εργαλείο αυτό ταυτίζεται με την έναρξη της τοκοφορίας, ενώ από
            14/09/2022 εφαρμόζεται ο ενιαίος μηχανισμός μεταβολών 1%. Στους
            τόκους επιδικίας αφαιρούνται οι ειδικές COVID αναστολές.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="principal">Κεφάλαιο (EUR)</Label>
                <Input
                  id="principal"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="π.χ. 25.683,93"
                  value={principal}
                  onBlur={handlePrincipalBlur}
                  onChange={(e) => handlePrincipalChange(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="yearBase">Βάση Έτους</Label>
                  <Popover>
                    <PopoverTrigger type="button">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </PopoverTrigger>
                    <PopoverContent className="w-80 text-sm">
                      Η χρήση ημερολογιακού έτους (365 ημέρες) είναι η ενδεδειγμένη, καθώς πρακτικές υπολογισμού με 360 ημέρες έχουν κριθεί καταχρηστικές από τα δικαστήρια.[1] Παράλληλα, η ΑΑΔΕ χρησιμοποιεί τις 365 ημέρες για τους υπολογισμούς της.
                    </PopoverContent>
                  </Popover>
                </div>
                <Select value={yearBase} onValueChange={setYearBase}>
                  <SelectTrigger id="yearBase">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="365">
                      365 ημέρες (Ημερολογιακό)
                    </SelectItem>
                    <SelectItem value="360">
                      360 ημέρες (Εμπορικό)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="startDate">
                    Ημερομηνία Επίδοσης / Έναρξης Τοκοφορίας
                  </Label>
                  <Popover>
                    <PopoverTrigger type="button">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </PopoverTrigger>
                    <PopoverContent className="w-80 text-sm">
                      Για το παρόν εργαλείο η ημερομηνία επίδοσης είναι και η
                      αφετηρία της τοκοφορίας. Δεν εισάγεται ξεχωριστή
                      ημερομηνία άσκησης ή κατάθεσης.
                    </PopoverContent>
                  </Popover>
                </div>
                <DatePicker
                  id="startDate"
                  value={startDate}
                  onChange={setStartDate}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">Ημερομηνία Λήξης / Εξόφλησης</Label>
                <DatePicker
                  id="endDate"
                  value={endDate}
                  onChange={setEndDate}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Label htmlFor="interestType">Τύπος Τόκου</Label>
                  <Popover>
                    <PopoverTrigger type="button">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </PopoverTrigger>
                    <PopoverContent className="w-80 text-sm">
                      Οι τόκοι επιδικίας χρησιμοποιούν το ίδιο επιτόκιο του
                      άρθρου 45 Ν. 4607/2019, αλλά δεν εκτοκίζονται στα
                      διαστήματα 13/03/2020-31/05/2020 και
                      07/11/2020-05/04/2021.
                    </PopoverContent>
                  </Popover>
                </div>
                <Select
                  value={interestType}
                  onValueChange={(value) => setInterestType(value as InterestType)}
                >
                  <SelectTrigger id="interestType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yperimerias">Τόκοι υπερημερίας</SelectItem>
                    <SelectItem value="epidikias">Τόκοι επιδικίας</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {error && (
              <p className="text-sm text-destructive font-medium">{error}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card>
          <CardHeader className="gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Αποτελέσματα Υπολογισμού</CardTitle>
              <CardDescription>
                Λήψη αρχείου Excel με ορατούς τύπους, αναφορές MRO και editable
                πεδία εισόδου.
              </CardDescription>
            </div>
            <Button
              type="button"
              onClick={handleExcelDownload}
              disabled={isExporting}
              className="sm:self-start"
            >
              <Download />
              {isExporting ? "Προετοιμασία Excel..." : "Λήψη Excel"}
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {exportError && (
              <p className="text-sm text-destructive font-medium">
                {exportError}
              </p>
            )}

            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Κεφάλαιο</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(result.principalAmount)} &euro;
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Συνολικός Τόκος</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(result.totalInterest)} &euro;
                </p>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm text-muted-foreground">
                  Συνολικό Οφειλόμενο
                </p>
                <p className="text-xl font-bold">
                  {formatCurrency(result.totalAmountDue)} &euro;
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Τύπος τόκου: {formatInterestType(result.interestType)}
            </p>
            <p className="text-sm text-muted-foreground">
              Βάση υπολογισμού: {result.baseDaysUsed} ημέρες
            </p>
            <p className="text-sm text-muted-foreground">
              MRO κατά την επίδοση/έναρξη: {formatRate(result.filingMroRate)}{" "}
              (ισχύει άμεσα μόνο έως 13/09/2022)
            </p>
            {result.suspendedDays > 0 && (
              <p className="text-sm text-muted-foreground">
                Ημέρες αναστολής τόκων επιδικίας: {result.suspendedDays}
              </p>
            )}

            {/* Breakdown Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Από</TableHead>
                    <TableHead>Έως</TableHead>
                    <TableHead className="text-right">Ημέρες</TableHead>
                    <TableHead className="text-right">Χρεώσιμες</TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <span>MRO</span>
                        <Popover>
                          <PopoverTrigger type="button">
                            <Info className="h-3 w-3 text-muted-foreground hover:text-primary transition-colors" />
                          </PopoverTrigger>
                          <PopoverContent className="w-80 text-sm font-normal">
                            Το MRO (επιτόκιο Κύριας Αναχρηματοδότησης της ΕΚΤ) συμμετέχει στον υπολογισμό και το τελικό επιτόκιο μεταβάλλεται μόνο εάν σημειωθεί σωρευτική διαφορά 1% σε σχέση με την προηγούμενη βάση υπολογισμού.
                          </PopoverContent>
                        </Popover>
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Επιτόκιο</TableHead>
                    <TableHead>Παρατήρηση</TableHead>
                    <TableHead className="text-right">Τόκος Περιόδου</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.periodBreakdown.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDate(row.fromDate)}</TableCell>
                      <TableCell>{formatDate(row.toDate)}</TableCell>
                      <TableCell className="text-right">{row.days}</TableCell>
                      <TableCell className="text-right">{row.chargeableDays}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {row.mroRate !== null ? formatRate(row.mroRate) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatRate(row.appliedRate)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.isInterestSuspended
                          ? row.suspensionReason ?? "Αναστολή τόκων επιδικίας"
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.interestAccrued)} &euro;
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7} className="font-semibold">
                      Σύνολο
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(result.totalInterest)} &euro;
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            {/* Detailed Legal Explanations Section */}
            <div className="mt-8 rounded-lg border bg-muted/30 p-6 space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">
                Νομικό & Νομολογιακό Πλαίσιο Υπολογισμού
              </h3>

              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  <strong className="text-foreground">
                    Άρθρο 45 Ν. 4607/2019 (Κανόνας 1%):
                  </strong>{" "}
                  Το επιτόκιο υπερημερίας του Δημοσίου ισούται με το επιτόκιο
                  MRO της Ευρωπαϊκής Κεντρικής Τράπεζας πλέον 3%, ωστόσο δεν
                  μεταβάλλεται συνεχώς, παρά μόνο όταν προκύψει σωρευτική
                  μεταβολή τουλάχιστον 1% από την προηγούμενη κλειδωμένη
                  βάση.[4]
                </p>

                <p>
                  <strong className="text-foreground">
                    Προϊσχύον Καθεστώς (έως 30/04/2019):
                  </strong>{" "}
                  Όπως παγίως κρίνει ο Άρειος Πάγος (π.χ. ΑΠ 875/2023, ΑΠ
                  249/2025), σε εκκρεμείς υποθέσεις εφαρμόζεται το σταθερό
                  επιτόκιο 6% για το χρονικό διάστημα έως την 30η Απριλίου
                  2019, και το νέο μεταβλητό επιτόκιο για το διάστημα από την
                  1η Μαΐου 2019 και εντεύθεν.
                </p>

                <p>
                  <strong className="text-foreground">
                    Αφετηρία Τοκοφορίας:
                  </strong>{" "}
                  Ο νόμος και η νομολογία (π.χ. ΑΠ 1262/2023) ορίζουν σαφώς ότι
                  η τοκοφορία ξεκινά αποκλειστικά από την επίδοση του σχετικού
                  δικογράφου (αγωγής) προς τον αρμόδιο φορέα του Δημοσίου.
                </p>

                <p>
                  <strong className="text-foreground">
                    Βάση Ημερών (365/360):
                  </strong>{" "}
                  Η χρήση εμπορικού έτους 360 ημερών αντί του ημερολογιακού
                  έτους 365 ημερών στους εκτοκισμούς έχει κριθεί ως
                  καταχρηστικός Γενικός Όρος Συναλλαγών που παραβιάζει την αρχή
                  της διαφάνειας (Εφ. Πειρ. 65/2021, ΤρΕφΑθ 6465/2025).
                  Παράλληλα, η Ανεξάρτητη Αρχή Δημοσίων Εσόδων (ΑΑΔΕ) στις
                  επίσημες φόρμες της διενεργεί τον υπολογισμό των τόκων
                  χρησιμοποιώντας τον παρονομαστή των 365 ημερών.
                </p>

                <p className="text-xs pt-2 border-t mt-4">
                  <strong>Σημείωση:</strong> Ο υπολογισμός αφορά αποκλειστικά
                  απλούς τόκους, χωρίς ανατοκισμό. Το MRO της επίδοσης
                  επηρεάζει άμεσα το επιτόκιο μόνο για το διάστημα έως
                  13/09/2022. Στους τόκους επιδικίας δεν υπολογίζεται τόκος
                  για τα διαστήματα αναστολής λόγω COVID που αναγνωρίζονται
                  στη νομολογία. Οι παραπάνω υπολογισμοί δεν εφαρμόζονται σε
                  απαιτήσεις επιστροφής φόρων (αρ. 53 Ν. 4174/2013), αξιώσεις
                  που στηρίζονται ευθέως στο ενωσιακό δίκαιο (π.χ.
                  καθυστερήσεις πληρωμών στις εμπορικές συναλλαγές), και σε
                  ειδικές συμβατικές ρήτρες με το Δημόσιο.
                </p>
              </div>
            </div>

          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Επιτόκια ΕΚΤ (MRO) ενημερωμένα έως{" "}
        {formatDate(calculator.getMroHistory().at(-1)!.date)}{" "}
        ·{" "}
        {mroSource === "api"
          ? "πηγή: Τράπεζα της Ελλάδος μέσω epitokia.mysolon.gr"
          : "ενσωματωμένα δεδομένα εφαρμογής"}
      </p>
    </div>
  );
}
